import { useState } from 'react'
import { notifications } from '@mantine/notifications'
import { useEmpresaLogoDelete, useEmpresaLogoUpload } from '../../hooks/useEmpresas'

const MAX_BYTES = 2 * 1024 * 1024
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml']

interface Props {
  empresaId: string
  logoUrl: string | null
}

export default function EmpresaLogoSection({ empresaId, logoUrl }: Props) {
  const upload = useEmpresaLogoUpload(empresaId)
  const remover = useEmpresaLogoDelete(empresaId)
  const [preview, setPreview] = useState<string | null>(null)
  const [arquivoSelecionado, setArquivoSelecionado] = useState<File | null>(null)

  function validar(file: File): string | null {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Formato não suportado. Use JPG, PNG ou SVG.'
    }
    if (file.size > MAX_BYTES) {
      return 'Tamanho máximo: 2MB.'
    }
    return null
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const erro = validar(file)
    if (erro) {
      notifications.show({ color: 'red', message: erro })
      e.target.value = ''
      return
    }
    setArquivoSelecionado(file)
    setPreview(URL.createObjectURL(file))
  }

  async function handleSalvar() {
    if (!arquivoSelecionado) return
    try {
      await upload.mutateAsync(arquivoSelecionado)
      setArquivoSelecionado(null)
      setPreview(null)
      notifications.show({ color: 'green', message: 'Logo atualizada.' })
    } catch (err) {
      notifications.show({
        color: 'red',
        message: err instanceof Error ? err.message : 'Falha ao enviar logo.',
      })
    }
  }

  function handleCancelar() {
    setArquivoSelecionado(null)
    setPreview(null)
  }

  async function handleRemover() {
    if (!confirm('Remover a logo desta empresa?')) return
    try {
      await remover.mutateAsync()
      notifications.show({ color: 'green', message: 'Logo removida.' })
    } catch {
      notifications.show({ color: 'red', message: 'Falha ao remover logo.' })
    }
  }

  const exibida = preview ?? logoUrl
  const imagemValida = !!exibida

  return (
    <section data-testid="empresa-logo-section">
      <h2 style={{ fontSize: '1.1rem', marginBottom: 12 }}>Logo da empresa</h2>
      <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: 12 }}>
        Aparece no cabeçalho de PMOCs, ordens de serviço e relatórios. Formatos aceitos: JPG,
        PNG ou SVG (máx. 2MB).
      </p>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div
          style={{
            width: 160,
            height: 160,
            border: '1px dashed #ccc',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fafafa',
            overflow: 'hidden',
          }}
        >
          {imagemValida ? (
            <img
              src={exibida ?? undefined}
              alt="Logo da empresa"
              data-testid="empresa-logo-preview"
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : (
            <span style={{ color: '#999', fontSize: '0.8rem' }}>Sem logo</span>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            type="file"
            data-testid="empresa-logo-input"
            accept="image/png,image/jpeg,image/svg+xml"
            onChange={handleFileChange}
            disabled={upload.isPending}
          />
          {arquivoSelecionado && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={handleSalvar}
                disabled={upload.isPending}
                data-testid="empresa-logo-salvar"
              >
                {upload.isPending ? 'Enviando...' : 'Salvar logo'}
              </button>
              <button type="button" onClick={handleCancelar} disabled={upload.isPending}>
                Cancelar
              </button>
            </div>
          )}
          {logoUrl && !arquivoSelecionado && (
            <button
              type="button"
              onClick={handleRemover}
              disabled={remover.isPending}
              data-testid="empresa-logo-remover"
              style={{ color: '#C62828' }}
            >
              {remover.isPending ? 'Removendo...' : 'Remover logo'}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}
